import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import {
  Box,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  Chip,
} from "@mui/material";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallbackPrompt } from "../../../../hooks/useCallbackPrompt";
import DialogBox from "../../../../components/DialogBox";
import {
  createNewVisit,
  getAllBusinessRules,
  getAllResonsService,
  getAllVisits,
  getPatientStatusTypesService,
} from "../../../../services/visits";
import "../StudySetup.css";
import DynamicNavigtion from "./DynamicNavigation";
import { buttonLinkStyle } from "./Questionnaires/EditVisitQuestionnaire";
import { handleBackNavigationGeneralFun } from "../../../../components/helpers/helper";

export const textBoxBody = {
  marginLeft: "10px",
};

export const textBoxStyles = {
  fontSize: 15,
  width: "500px",
  height: "15px",
  padding: "11px 14px",
};

export const requestFailed = (msg) =>
  toast.error(`${msg}`, {
    toastId: "requestFailed",
  });

const CreateVisit = () => {
  const navigate = useNavigate();

  const [load, setLoad] = React.useState(false);

  const [businessRulesData, setBusinessRulesData] = React.useState([]);
  const [allReasons, setAllReasons] = useState([]);
  const [allPatientStatus, setAllPatientStatus] = useState([]);
  const [allVisits, setAllVisits] = useState([]);
  const [selectedVisitNavigations, setSelectedNavigation] = useState([]);

  const [showDialog, setShowDialog] = React.useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);

  const notify = () =>
    toast.success("Visit Created Successfully", {
      toastId: "form-creation",
    });

  // .default("2023-02-15T09:43:40.377Z")

  const createVisitSchema = yup.object().shape({
    name: yup
      .string()
      .required("This field is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Value must not start with a number or special character"
      )
      .test("has-letter", "Value must contain at least one alphabet", (value) =>
        /[A-Za-z]/.test(value || "")
      ),
    daysExpected: yup
      .number()
      .typeError("Value must be a number")
      .required("This field is required")
      .min(0, "Value cannot be less than 0")
      .max(999999999, "Value cannot be greater than 999999999"),
    windowBefore: yup
      .number()
      .typeError("Value must be a number")
      .required("This field is required")
      .min(0, "Value cannot be less than 0")
      .max(999999999, "Value cannot be greater than 999999999"),
    windowAfter: yup
      .number()
      .typeError("Value must be a number")
      .required("This field is required")
      .min(0, "Value cannot be less than 0")
      .max(999999999, "Value cannot be greater than 999999999"),
    notes: yup.string().required("This field is required"),
    lastUpdate: yup.string().required("This field is required"),
    windowOverride: yup.bool().oneOf([true, false]),
    isScheduled: yup.bool().oneOf([true, false]),
    interviewMode: yup.bool().oneOf([true, false]),
    closeOut: yup.bool().oneOf([true, false]),
    alwaysAvailable: yup.bool().oneOf([true, false]),
    reasons: yup
      .array()
      .min(1, "Select at least one reason") // Ensure at least one selection
      .required("Reasons is required"),
    visitOrder: yup
      .number()
      .typeError("Value must be a number")
      .required("This field is required")
      .min(0, "Value cannot be less than 0")
      .max(999999999, "Value cannot be greater than 999999999"),
    visitAnchor: yup
      .number()
      .typeError("Value must be a number")
      .required("This field is required")
      .min(0, "Value cannot be less than 0")
      .max(999999999, "Value cannot be greater than 999999999"),
    circularVisitRepeatCount: yup
      .number()
      .typeError("Value must be a number")
      .required("This field is required")
      .min(0, "Value cannot be less than 0")
      .max(999999999, "Value cannot be greater than 999999999"),
    overrideReason: yup.string().required("This field is required"),
    visitStopHsn: yup
      .string()
      .required("This field is required")
      .matches(/^[a-zA-Z0-9]+$/, "Only alphabets and numbers are allowed"),
    dosageModule: yup.bool().oneOf([true, false]),
    interviewModeAvailable: yup.bool().oneOf([true, false]),
    closeoutFormAvailable: yup.bool().oneOf([true, false]),
    circularVisit: yup.bool().oneOf([true, false]),
    dosageModuleEnabled: yup.bool().oneOf([true, false]),
    visitAvailableBusinessRuleId: yup
      .string()
      .required("This field is required"),
  });

  const {
    register,
    setValue,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createVisitSchema),
  });

  // console.log("createdNav", selectedVisitNavigations);

  const onSubmit = async (data) => {
    // console.log(data);
    try {
      setShowDialog(false);
      setLoad(true);

      const {
        name,
        daysExpected,
        windowBefore,
        windowAfter,
        notes,
        windowOverride,
        isScheduled,
        interviewMode,
        lastUpdate,
        closeOut,
        overrideReason,
        alwaysAvailable,
        reasons,
        visitOrder,
        visitAnchor,
        visitStopHsn,
        visitAvailableBusinessRuleId,
        interviewModeAvailable,
        closeoutFormAvailable,
        circularVisit,
        circularVisitRepeatCount,
        dosageModuleEnabled,
      } = data;

      const newData = {
        name,
        daysExpected,
        windowBefore,
        windowAfter,
        notes,
        lastUpdate,
        windowOverride,
        isScheduled,
        interviewMode,
        closeOut,
        reasons,
        alwaysAvailable,
        overrideReason,
        visitOrder,
        visitAnchor,
        visitStopHsn,
        interviewModeAvailable,
        closeoutFormAvailable,
        circularVisit,
        circularVisitRepeatCount,
        dosageModuleEnabled,
        visitAvailableBusinessRuleId,
      };
      newData.visitNavigations = selectedVisitNavigations;

      const res = await createNewVisit(newData);
      if (res.status) {
        setLoad(false);
        notify();
        navigate("/study-management/visits");
      }
    } catch (err) {
      setLoad(false);
      setShowDialog(false);
      console.log("Error: ", err.message);
      requestFailed(err.message);
    }
  };

  const fetchBusinessData = async () => {
    try {
      const res = await getAllBusinessRules();
      if (res.status) {
        setBusinessRulesData(res?.data?.result);
      }
    } catch (err) {
      console.log("Error: ", err.message);
      requestFailed(err.message);
    }
  };

  const fetchAllReasons = async () => {
    try {
      const res = await getAllResonsService();
      if (res.status) {
        setAllReasons(res?.data?.result);
      }
    } catch (err) {
      console.log("Error: ", err.message);
      requestFailed(err.message);
    }
  };

  const fetchAllVisits = async () => {
    try {
      const res = await getAllVisits();
      if (res.status) {
        setAllVisits(res?.data?.result);
      }
    } catch (err) {
      console.log("Error: ", err.message);
      requestFailed(err.message);
    }
  };

  const fetchAllPatientStatus = async () => {
    try {
      const res = await getPatientStatusTypesService();
      if (res.status) {
        setAllPatientStatus(res?.data?.result);
      }
    } catch (err) {
      console.log("Error: ", err.message);
      requestFailed(err.message);
    }
  };

  React.useEffect(() => {
    fetchBusinessData();
    fetchAllReasons();
    fetchAllPatientStatus();
    fetchAllVisits();
  }, []);

  const handleCancel = (e) => {
    e.preventDefault();

    if (showDialog) {
      const confirmExit = window?.confirm(
        "You have unsaved changes, Are you sure you want to leave?"
      );

      if (confirmExit) {
        navigate("/study-management/visits");
      }
    } else {
      navigate("/study-management/visits");
    }
  };

  const loadContainerStyles = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "70vh",
  };

  const labelStyles = {
    display: "flex",
    alignItems: "center",
    height: "20px",
  };

  const texAreaStyles = {
    fontSize: 15,
    width: "500px",
    height: "40px",
  };

  const selectStyles = {
    width: 530,
    marginTop: 1,
  };

  const rowStyles = {
    marginTop: "2%",
  };

  return (
    <>
      {load ? (
        <>
          <div style={loadContainerStyles}>
            <BeatLoader color="#3661eb" />
          </div>
        </>
      ) : (
        <div className="content-body">
          <p className="study-management-link" style={{ fontWeight: "600" }}>
            <button
              style={buttonLinkStyle}
              onClick={(e) =>
                handleBackNavigationGeneralFun(
                  e,
                  showDialog,
                  "study-management",
                  navigate
                )
              }
            >
              Manage Your Study
            </button>{" "}
            |{" "}
            <button
              style={buttonLinkStyle}
              onClick={(e) =>
                handleBackNavigationGeneralFun(
                  e,
                  showDialog,
                  "study-management/visits",
                  navigate
                )
              }
            >
              Visits
            </button>{" "}
            | <Link to="/study-management/visits/create">Create Visit</Link>-{" "}
            <b style={{ color: "gray" }}>
              {sessionStorage?.getItem("studyName")}
            </b>
          </p>

          <div>
            <DialogBox
              showDialog={showPrompt}
              confirmNavigation={confirmNavigation}
              cancelNavigation={cancelNavigation}
            />
          </div>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ height: "auto", width: "100%" }}
            autoComplete="off"
          >
            <div className="createVisitBody">
              <div className="createVisitHeader">
                <h1>Create Visit</h1>
              </div>
              <div className="createVisitFormBody">
                <div className="createVisitInput">
                  <p style={labelStyles}>
                    Name
                    <span className="error-highlight">*</span>
                  </p>
                  <TextField
                    type={"text"}
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                    }}
                    {...register("name", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.name && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.name.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitInput">
                  <p style={labelStyles}>
                    Days Expected
                    <span className="error-highlight">*</span>
                  </p>
                  <TextField
                    type={"number"}
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                      min: 0,
                    }}
                    {...register("daysExpected", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.daysExpected && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.daysExpected.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitInput">
                  <p>
                    Window Before
                    <span className="error-highlight">*</span>
                  </p>
                  <TextField
                    type={"number"}
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                      min: 0,
                    }}
                    {...register("windowBefore", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.windowBefore && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.windowBefore.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitInput">
                  <p>
                    Window After
                    <span className="error-highlight">*</span>
                  </p>
                  <TextField
                    type={"number"}
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                      min: 0,
                    }}
                    {...register("windowAfter", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.windowAfter && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.windowAfter.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitInput">
                  <p>
                    Circular Visit Repeat Count
                    <span className="error-highlight">*</span>
                  </p>
                  <TextField
                    type={"number"}
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                      min: 0,
                    }}
                    {...register("circularVisitRepeatCount", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.circularVisitRepeatCount && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.circularVisitRepeatCount.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitInput">
                  <p>
                    Notes
                    <span className="error-highlight">*</span>
                  </p>
                  <TextField
                    multiline
                    rows={1}
                    inputProps={{
                      style: texAreaStyles,
                    }}
                    {...register("notes", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.notes && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.notes.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitInput">
                  <p>
                    Last Update
                    <span className="error-highlight">*</span>
                  </p>
                  <TextField
                    style={textBoxBody}
                    type="datetime-local"
                    inputProps={{
                      style: textBoxStyles,
                    }}
                    {...register("lastUpdate", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.lastUpdate && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.lastUpdate.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitCheckbox">
                  <p>
                    Window Override
                    {/* <span className="error-highlight">*</span> */}
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    // checked={checked}
                    // onChange={handleChange}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("windowOverride", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.windowOverride && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.windowOverride.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitInput">
                  <p>
                    Override Reason
                    <span className="error-highlight">*</span>
                  </p>
                  <TextField
                    type={"text"}
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                      min: 0,
                    }}
                    {...register("overrideReason", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>
                {errors.overrideReason && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.overrideReason.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="createVisitCheckbox">
                  <p>
                    Is Scheduled {/* {errors.isScheduled?.message ? ( */}
                    {/* <span className="error-highlight">*</span> */}
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("isScheduled", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.isScheduled && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.isScheduled.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitCheckbox">
                  <p>
                    Interview Mode Available{" "}
                    {/* {errors.interviewMode?.message ? ( */}
                    {/* <span className="error-highlight">*</span> */}
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("interviewModeAvailable", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.interviewModeAvailable && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.interviewModeAvailable.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitCheckbox">
                  <p>
                    Closeout Form Available{" "}
                    {/* {errors.closeOut?.message ? ( */}
                    {/* <span className="error-highlight">*</span> */}
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("closeoutFormAvailable", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.closeoutFormAvailable && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.closeoutFormAvailable.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitCheckbox">
                  <p>Always Available </p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("alwaysAvailable", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.alwaysAvailable && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.alwaysAvailable.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitInput">
                  <p style={labelStyles}>
                    Reasons {/* {errors.name?.message ? ( */}
                    <span className="error-highlight">*</span>
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <FormControl sx={selectStyles}>
                    <Select
                      labelId="reasons-label"
                      name="reasons"
                      multiple
                      value={getValues("reasons") || []} // Use getValues to get the current selected values
                      inputProps={{
                        style: textBoxStyles,
                        "aria-label": "Reasons",
                        className: "custom-input-class",
                      }}
                      onChange={(e) => {
                        setValue("reasons", e.target.value, {
                          shouldValidate: true,
                        });
                        setShowDialog(true);
                      }}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {/* {console.log("selsc", selected)} */}
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={
                                allReasons?.find(
                                  (item) => item?.value === value
                                )?.title
                              }
                              sx={{ fontSize: "12px" }}
                            />
                          ))}
                        </Box>
                      )}
                      // MenuProps={MenuProps}
                    >
                      {/* <MenuItem>
                        <em>None</em>
                      </MenuItem> */}
                      {allReasons?.map((item, index) => (
                        <MenuItem key={index} value={item.value}>
                          {item.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {errors.reasons && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.reasons.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitInput">
                  <p>
                    Visit Order {/* {errors.visitOrder?.message ? ( */}
                    <span className="error-highlight">*</span>
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <TextField
                    type={"number"}
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                      min: 0,
                    }}
                    {...register("visitOrder", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.visitOrder && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.visitOrder.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitInput">
                  <p>
                    Visit Anchor {/* {errors.visitAnchor?.message ? ( */}
                    <span className="error-highlight">*</span>
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <TextField
                    type={"number"}
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                      min: 0,
                    }}
                    {...register("visitAnchor", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.visitAnchor && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.visitAnchor.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitInput">
                  <p>
                    Visitstop_HSN {/* {errors.visitStopHsn?.message ? ( */}
                    <span className="error-highlight">*</span>
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <TextField
                    style={textBoxBody}
                    inputProps={{
                      style: textBoxStyles,
                      maxLength: 1,
                    }}
                    {...register("visitStopHsn", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.visitStopHsn && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.visitStopHsn.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitCheckbox">
                  <p>Dosage Module Enabled</p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("dosageModuleEnabled", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.dosageModuleEnabled && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitInput">
                        <p
                          style={{
                            marginTop: "10px",
                            color: "blue",
                          }}
                        >
                          {errors.dosageModuleEnabled.message}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* <div className="createVisitCheckbox">
                  <p>Interview Mode Available</p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("interviewModeAvailable", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.interviewModeAvailable && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitInput">
                        <p
                          style={{
                            marginTop: "10px",
                            color: "blue",
                          }}
                        >
                          {errors.interviewModeAvailable.message}
                        </p>
                      </div>
                    </div>
                  </>
                )} */}

                {/* <div className="createVisitCheckbox">
                  <p>Closeout Form Available</p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("closeoutFormAvailable", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.closeoutFormAvailable && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitInput">
                        <p
                          style={{
                            marginTop: "10px",
                            color: "blue",
                          }}
                        >
                          {errors.closeoutFormAvailable.message}
                        </p>
                      </div>
                    </div>
                  </>
                )} */}

                <div className="createVisitCheckbox">
                  <p>Circular Visit</p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("circularVisit", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.circularVisit && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitInput">
                        <p
                          style={{
                            marginTop: "10px",
                            color: "blue",
                          }}
                        >
                          {errors.circularVisit.message}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* <div className="createVisitCheckbox">
                  <p>Dosage Module Enabled</p>
                  <Checkbox
                    style={{ fontSize: "15px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("dosageModuleEnabled", {
                      onChange: (e) => {
                        setShowDialog(true);
                      },
                    })}
                  />
                </div>

                {errors.dosageModuleEnabled && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitInput">
                        <p
                          style={{
                            marginTop: "10px",
                            color: "blue",
                          }}
                        >
                          {errors.dosageModuleEnabled.message}
                        </p>
                      </div>
                    </div>
                  </>
                )} */}

                <div className="createVisitInput">
                  <p style={labelStyles}>
                    Visit Available Business Rule{" "}
                    {/* {errors.name?.message ? ( */}
                    <span className="error-highlight">*</span>
                    {/* ) : (
                      <></>
                    )} */}
                  </p>
                  <FormControl sx={selectStyles}>
                    <Select
                      name="visitAvailableBusinessRuleId"
                      defaultValue={""}
                      inputProps={{
                        style: textBoxStyles,
                        "aria-label": "Without label",
                        className: "custom-input-class",
                      }}
                      {...register("visitAvailableBusinessRuleId", {
                        onChange: (e) => {
                          setValue(
                            "visitAvailableBusinessRuleId",
                            e.target.value,
                            {
                              shouldValidate: true,
                            }
                          );
                          setShowDialog(true);
                        },
                      })}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {businessRulesData.map((item, index) => (
                        <MenuItem key={index} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                {errors.visitAvailableBusinessRuleId && (
                  <>
                    <div className="createVisitFlexEnd">
                      <div className="createVisitError">
                        <span className="error-text" style={{ color: "red" }}>
                          {errors.visitAvailableBusinessRuleId.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="createVisitInput">
                  <p>Visit Navigations</p>
                  <div style={{ width: "100vw", marginLeft: "20px" }}>
                    <DynamicNavigtion
                      allVisits={allVisits}
                      patientStatus={allPatientStatus}
                      setSelectedNavigation={setSelectedNavigation}
                      setShowDialog={setShowDialog}
                    />
                  </div>
                  {/* <Link className="navigationLink">Add Visit Navigation</Link> */}
                </div>

                <Row style={rowStyles}>
                  <Col md={8}></Col>
                  <Col md={2}>
                    <div className="study-management-head-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleCancel(e);
                        }}
                        className="study-management-cancel-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </Col>
                  <Col md={2}>
                    <div className="study-management-head-end">
                      <button
                        type="submit"
                        className="study-management-create-btn-md"
                      >
                        Save
                      </button>
                    </div>
                  </Col>
                </Row>

                {/* <div className="createVisitButtonBody">
                  <button
                    onClick={(e) => {
                      handleCancel(e);
                    }}
                    className="createVisitCancelBtn"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="createVisitSaveBtn">
                    Save
                  </button>
                </div> */}
              </div>
            </div>
          </Box>
        </div>
      )}
    </>
  );
};

export default CreateVisit;
